import React, { useEffect, useState, useCallback, useRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { QrCode, ExternalLink, Download, FolderOpen, Eye, Share2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import DocViewer from "../components/DocViewer";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";
const slug = (s) => (s || "qr").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 40) || "qr";

// If the QR target points at one of our own media files (e.g. an uploaded flyer),
// return an api-relative path so it can be previewed full-screen in-app.
const mediaPathOf = (val) => {
  const m = (val || "").match(/\/(?:api\/)?media\/([a-f0-9]{8,})/i);
  return m ? `/media/${m[1]}` : "";
};

function SharedCard({ qr, origin, onPreview }) {
  const ref = useRef(null);
  const trackUrl = `${origin}/api/qr/${qr.id}`;          // for the QR image (scan) + sharing
  const openUrl = `${trackUrl}?via=portal`;               // for the button (counts a member open)
  const previewPath = mediaPathOf(qr.value);
  const download = () => {
    const canvas = ref.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png"); a.download = `${slug(qr.name)}-qr.png`;
    document.body.appendChild(a); a.click(); a.remove();
  };
  const share = async () => {
    const text = `${qr.name}${qr.caption ? " — " + qr.caption : ""}`;
    if (navigator.share) {
      try { await navigator.share({ title: qr.name, text, url: trackUrl }); }
      catch (e) { /* user cancelled */ }
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${trackUrl}`)}`, "_blank", "noopener");
      toast.success("Opening WhatsApp to share…");
    }
  };
  return (
    <div className={`${CARD} p-6 flex flex-col`} data-testid={`shared-qr-${qr.id}`}>
      <div className="flex items-start gap-2.5 mb-2">
        <span className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#0B5E4E1A" }}>
          <QrCode className="h-5 w-5" style={{ color: "var(--sj-primary)" }} />
        </span>
        <div className="min-w-0 flex-1">
          <h2 className="font-head text-lg font-bold truncate" style={{ color: "var(--sj-text)" }}>{qr.name}</h2>
          {qr.category && <span className="inline-block mt-0.5 text-[10px] font-semibold uppercase tracking-wider rounded-full px-2 py-0.5" style={{ background: "#0B5E4E1A", color: "var(--sj-primary)" }}>{qr.category}</span>}
        </div>
      </div>
      {qr.caption && <p className="text-sm mb-1" data-testid={`shared-qr-caption-${qr.id}`} style={{ color: "var(--sj-text-2)" }}>{qr.caption}</p>}
      <div className="flex justify-center py-2" ref={ref}>
        <div className="p-3.5 rounded-2xl border-2" style={{ borderColor: "var(--sj-primary)" }}>
          <QRCodeCanvas value={trackUrl} size={180} level="M" includeMargin={false} fgColor="#0B5E4E" />
        </div>
      </div>
      <p className="text-xs text-center mt-2" style={{ color: "var(--sj-text-3)" }}>Scan with your camera, or tap a button below.</p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
        {previewPath ? (
          <button type="button" onClick={() => onPreview(qr, previewPath)} data-testid={`shared-qr-preview-${qr.id}`}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
            <Eye className="h-4 w-4" /> View flyer
          </button>
        ) : (
          <a href={openUrl} target="_blank" rel="noopener noreferrer" data-testid={`shared-qr-open-${qr.id}`}
            className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white hover:brightness-105" style={{ background: "var(--sj-primary)" }}>
            <ExternalLink className="h-4 w-4" /> Open link
          </a>
        )}
        <button type="button" onClick={share} data-testid={`shared-qr-share-${qr.id}`}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border hover:bg-[var(--sj-bg)]" style={{ borderColor: "var(--sj-primary)", color: "var(--sj-primary)" }}>
          <Share2 className="h-4 w-4" /> Share
        </button>
        <button type="button" onClick={download} data-testid={`shared-qr-download-${qr.id}`}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border hover:bg-[var(--sj-bg)]" style={{ borderColor: "var(--sj-border)", color: "var(--sj-text-2)" }}>
          <Download className="h-4 w-4" /> Save image
        </button>
      </div>
    </div>
  );
}

export default function SharedQR() {
  const origin = window.location.origin;
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);   // { qr, path }
  const [activeCat, setActiveCat] = useState("All");
  const load = useCallback(async () => {
    try { const { data } = await api.get("/portal/published-qr-codes"); setCodes(data || []); }
    catch (e) { toast.error("Could not load QR codes"); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openPreview = (qr, path) => {
    setPreview({ qr, path });
    // Count this as a member open (mirrors the tracked "Open link" for non-flyer QRs).
    api.get(`/qr/${qr.id}?via=portal`).catch(() => {});
  };

  const categories = Array.from(new Set(codes.map((c) => c.category || "General")));
  const shown = activeCat === "All" ? codes : codes.filter((c) => (c.category || "General") === activeCat);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="shared-qr-page">
      <div className="sj-page-hero">
        <h1 className="font-head text-3xl font-extrabold flex items-center gap-2" style={{ color: "var(--sj-text)" }}>
          <QrCode className="h-7 w-7" style={{ color: "var(--sj-primary)" }} /> Church QR Codes
        </h1>
        <p className="mt-1 text-sm" style={{ color: "var(--sj-text-2)" }}>
          Handy QR codes shared by the church team — scan or tap to open, share to a friend, or save the image.
        </p>
      </div>

      {loading ? (
        <div className={`${CARD} p-10 text-center text-sm`} style={{ color: "var(--sj-text-3)" }}>Loading…</div>
      ) : codes.length === 0 ? (
        <div className={`${CARD} p-10 text-center`} data-testid="shared-qr-empty">
          <FolderOpen className="h-8 w-8 mx-auto mb-3" style={{ color: "var(--sj-text-3)" }} />
          <p className="text-sm font-semibold" style={{ color: "var(--sj-text-2)" }}>No QR codes shared yet</p>
          <p className="text-sm mt-1" style={{ color: "var(--sj-text-3)" }}>When the church team shares a QR code, it will appear here.</p>
        </div>
      ) : (
        <>
          {categories.length > 1 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="shared-qr-cats">
              {["All", ...categories].map((c) => (
                <button key={c} type="button" data-testid={`shared-qr-cat-${slug(c)}`} onClick={() => setActiveCat(c)}
                  className="px-4 py-2 rounded-full text-sm font-semibold transition-colors"
                  style={activeCat === c ? { background: "var(--sj-primary)", color: "#fff" } : { color: "var(--sj-text-2)", border: "1px solid var(--sj-border)", background: "#fff" }}>
                  {c}
                </button>
              ))}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="shared-qr-grid">
            {shown.map((qr) => <SharedCard key={qr.id} qr={qr} origin={origin} onPreview={openPreview} />)}
          </div>
        </>
      )}

      <DocViewer open={!!preview} onClose={() => setPreview(null)}
        path={preview?.path} fileName={preview ? `${slug(preview.qr.name)}` : "flyer"} />
    </div>
  );
}
